import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Announcement, Button, Input, ModalV2 as Modal, Select } from '@utilities';

import styles from './_index.module.scss';

const ModalPurchaseVolume = ({
  currentValues = [],
  initialSelectedField,
  isOpen,
  onClose,
  onSave,
  surveyComparisonOptions,
  uniqueBrandsPrompt,
  volumePrompt,
}) => {
  const [operatorByField, setOperatorByField] = useState({});
  const [rangeError, setRangeError] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [valueByField, setValueByField] = useState({});

  const allAttributes = useMemo(() => {
    return [
      ...(volumePrompt?.attributes?.map((attr) => ({ ...attr, promptId: volumePrompt.id })) || []),
      ...(uniqueBrandsPrompt?.attributes?.map((attr) => ({
        ...attr,
        promptId: uniqueBrandsPrompt.id,
      })) || []),
    ];
  }, [volumePrompt, uniqueBrandsPrompt]);

  useEffect(() => {
    const opMap = {};
    const valMap = {};

    currentValues.forEach(({ field, operator, values }) => {
      opMap[field] = operator;
      valMap[field] = Array.isArray(values) ? values : [values];
    });

    const defaultFieldId = initialSelectedField?.field || allAttributes[0]?.id;

    if (defaultFieldId && !opMap[defaultFieldId]) {
      opMap[defaultFieldId] = surveyComparisonOptions[0].value;
      valMap[defaultFieldId] = '';
    }

    setOperatorByField(opMap);
    setValueByField(valMap);
    setSelectedFieldId(defaultFieldId);
  }, [currentValues, initialSelectedField, allAttributes]);

  useEffect(() => {
    if (!selectedFieldId) return;

    setOperatorByField((prev) => {
      if (prev[selectedFieldId]) return prev;
      return { ...prev, [selectedFieldId]: surveyComparisonOptions[0].value };
    });

    setValueByField((prev) => {
      if (prev[selectedFieldId] !== undefined) return prev;
      return { ...prev, [selectedFieldId]: '' };
    });
  }, [selectedFieldId, allAttributes]);

  const handleOperatorChange = ({ value }) => {
    setOperatorByField((prev) => ({ ...prev, [selectedFieldId]: value }));
    setValueByField((prev) => ({
      ...prev,
      [selectedFieldId]: value === 'IN_RANGE' ? ['', ''] : '',
    }));
  };

  const handleRangeValueChange = ({ index, value }) => {
    const operator = operatorByField[selectedFieldId];

    setValueByField((prev) => {
      if (operator === 'IN_RANGE') {
        const prevValue = prev[selectedFieldId] || ['', ''];
        const updated = [...prevValue];
        updated[index] = value;
        return { ...prev, [selectedFieldId]: updated };
      }

      if (index === 0) {
        return { ...prev, [selectedFieldId]: [value] };
      }

      return prev;
    });
  };

  const handleSave = () => {
    setRangeError(false);

    let hasInvalidRange = false;

    const answers = allAttributes
      .filter(({ id }) => {
        const op = operatorByField[id];
        const val = valueByField[id];

        if (!op) return false;

        if (op === 'IN_RANGE') {
          const [min, max] = val || [];
          const minNum = parseFloat(min);
          const maxNum = parseFloat(max);
          const valid =
            min?.toString().trim() &&
            max?.toString().trim() &&
            !isNaN(minNum) &&
            !isNaN(maxNum) &&
            minNum <= maxNum;

          if (!valid) hasInvalidRange = true;
          return valid;
        }

        return val?.[0]?.toString().trim();
      })
      .map(({ id, promptId }) => {
        const op = operatorByField[id];
        const raw = valueByField[id];
        const values = Array.isArray(raw) ? raw : [raw];

        return {
          field: id,
          fieldType: 'NUMERIC',
          operator: op,
          promptId,
          values,
        };
      });

    if (hasInvalidRange) {
      setRangeError(true);
      return;
    }

    onSave(answers);
    onClose();
  };

  const selectedAttribute = allAttributes.find(({ id }) => id === selectedFieldId);
  const currentOpValue = operatorByField[selectedFieldId];
  const currentOpObj =
    surveyComparisonOptions.find(({ value }) => value === currentOpValue) ||
    surveyComparisonOptions[0];

  const isApplyDisabled =
    !currentOpValue ||
    (currentOpValue === 'IN_RANGE'
      ? !(
          Array.isArray(valueByField[selectedFieldId]) &&
          valueByField[selectedFieldId][0]?.toString().trim() &&
          valueByField[selectedFieldId][1]?.toString().trim()
        )
      : !valueByField[selectedFieldId]?.[0]?.toString().trim());

  return (
    <Modal
      buttons={[
        <Button key="cancel" onClick={onClose} text="Cancel" variant="secondary" />,
        <Button isDisabled={isApplyDisabled} key="save" onClick={handleSave} text="Apply" />,
      ]}
      isActive={isOpen}
      onClose={onClose}
      sidebar={
        <div className={styles['modal-purchase-volume-sidebar']}>
          <h4>Purchase Volume</h4>
          <ul>
            {volumePrompt.attributes
              .filter(({ isPlaceholder }) => !isPlaceholder)
              .map(({ id, name }) => (
                <li key={id}>
                  <button
                    className={selectedFieldId === id ? styles['is-active'] : ''}
                    onClick={() => setSelectedFieldId(id)}
                  >
                    {name}
                  </button>
                </li>
              ))}
          </ul>
          <hr />
          <h4>Unique Brands</h4>
          <ul>
            {uniqueBrandsPrompt.attributes.map(({ id, name }) => (
              <li key={id}>
                <button
                  className={selectedFieldId === id ? styles['is-active'] : ''}
                  onClick={() => setSelectedFieldId(id)}
                >
                  {name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      }
      size="large"
      title={selectedAttribute?.name}
    >
      {rangeError && (
        <Announcement
          hasClose
          text="Please ensure all ranges have valid min and max values (min must be less than or equal to max)."
          variant="error"
        />
      )}
      <div className={styles['modal-purchase-volume-container']}>
        <div className={styles['modal-purchase-volume-row']}>
          <Select
            className={styles['modal-purchase-volume-select']}
            onChange={handleOperatorChange}
            options={surveyComparisonOptions}
            value={currentOpObj}
          />
          <span className={styles['modal-purchase-volume-label']}>the value of</span>
          {currentOpValue === 'IN_RANGE' ? (
            <div className={styles['modal-purchase-volume-between']}>
              <Input
                className={styles['modal-purchase-volume-input']}
                onChange={(value) => handleRangeValueChange({ index: 0, value })}
                placeholder="Min"
                type="number"
                value={valueByField[selectedFieldId]?.[0] || ''}
              />
              <span className={styles['modal-purchase-volume-and']}>and</span>
              <Input
                className={styles['modal-purchase-volume-input']}
                onChange={(value) => handleRangeValueChange({ index: 1, value })}
                placeholder="Max"
                type="number"
                value={valueByField[selectedFieldId]?.[1] || ''}
              />
            </div>
          ) : (
            <Input
              className={styles['modal-purchase-volume-input']}
              onChange={(value) => handleRangeValueChange({ index: 0, value })}
              placeholder="Enter value"
              type="number"
              value={valueByField[selectedFieldId]?.[0] || ''}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

ModalPurchaseVolume.propTypes = {
  currentValues: PropTypes.array,
  initialSelectedField: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  surveyComparisonOptions: PropTypes.array,
  uniqueBrandsPrompt: PropTypes.object.isRequired,
  volumePrompt: PropTypes.object.isRequired,
};

export default ModalPurchaseVolume;
